package com.arenaproplay.admin.pages

import androidx.compose.runtime.*
import com.arenaproplay.admin.di.LocalAppContainer
import com.arenaproplay.admin.domain.admin.NewsArticleRow
import com.arenaproplay.admin.domain.admin.NewsImageCandidates
import com.arenaproplay.admin.domain.admin.NewsMatchLink
import com.arenaproplay.admin.domain.admin.SaveNewsArticlePayload
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.layouts.AdminLayout
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.toRgbaString
import com.arenaproplay.admin.ui.utils.pickImageFile
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.TextArea

// Dark-theme form palette (mirrors AppLaunch / SearchInput).
private const val INPUT_BG = "#0B1220"
private const val INPUT_BORDER = "1px solid rgba(255,255,255,0.10)"

private val STATUSES = listOf("DRAFT", "PUBLISHED", "ARCHIVED")

// Curated "most discussed" strip — its own tab (not a real article status).
private const val FEATURED_TAB = "FEATURED"
private val TABS = STATUSES + FEATURED_TAB

private fun tabLabel(tab: String) =
    if (tab == FEATURED_TAB) "Обсуждаемое" else tab.lowercase().replaceFirstChar { it.uppercase() }

private val SPORTS = listOf("football", "basketball", "tennis", "volleyball", "padel", "mma", "formula1", "boxing", "other")
private val TYPES = listOf("NEWS", "SPECIAL")

/**
 * Arena News moderation: the ingestion worker files RSS+AI drafts; here the
 * admin edits the English master, picks a legal cover and publishes —
 * publishing also fires the translation pass into every UI locale.
 */
@Page("/news")
@Composable
fun NewsPage() {
    val repo = LocalAppContainer.current.adminRepository ?: return
    val scope = rememberCoroutineScope()
    val loadState = rememberLoadState()

    var statusTab by remember { mutableStateOf("DRAFT") }
    var page by remember { mutableStateOf(1) }
    var totalPages by remember { mutableStateOf(1) }
    var items by remember { mutableStateOf<List<NewsArticleRow>?>(null) }
    var editing by remember { mutableStateOf<NewsArticleRow?>(null) }
    var busy by remember { mutableStateOf(false) }
    var actionMessage by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(loadState.reloadToken, statusTab, page) {
        loadState.load {
            if (statusTab == FEATURED_TAB) {
                items = repo.getFeaturedNews()
                totalPages = 1
            } else {
                val result = repo.getNewsArticles(statusTab, page)
                items = result.items
                totalPages = result.totalPages.coerceAtLeast(1)
            }
        }
    }

    AdminLayout(
        title = "Новости",
        subtitle = "Очередь черновиков из RSS+AI — правка, обложка, публикация с переводом",
    ) {
        if (loadState.error != null) {
            ErrorCard(loadState)
            return@AdminLayout
        }

        AdminCard {
            // ── Toolbar: status tabs · ingest · pagination ──
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                TABS.forEach { status ->
                    val active = statusTab == status
                    Box(
                        Modifier
                            .sBorderRadius(999)
                            .padding(leftRight = 14.px, topBottom = 7.px)
                            .margin(right = 8.px)
                            .styleModifier {
                                property("background", if (active) "#0A1E42" else "#101828")
                                property(
                                    "border",
                                    if (active) "1px solid rgba(143,184,255,0.45)" else "1px solid rgba(255,255,255,0.10)",
                                )
                                property("cursor", "pointer")
                            }
                            .onClick {
                                statusTab = status
                                page = 1
                                editing = null
                                loadState.retry()
                            },
                    ) {
                        SpanText(
                            tabLabel(status),
                            Modifier.bodySMedium().color(if (active) AppColors.Ink else AppColors.Muted),
                        )
                    }
                }
                Box(Modifier.weight(1f))
                actionMessage?.let {
                    SpanText(it, Modifier.bodySMedium().color(AppColors.Muted).margin(right = 12.px))
                }
                ActionButton("Переоценить", enabled = !busy) {
                    busy = true
                    actionMessage = "Запускаю переоценку…"
                    scope.launch {
                        try {
                            val report = repo.runNewsRescore()
                            actionMessage = "Переоценка ${report.queued} черновиков запущена — обнови через 1-2 мин"
                        } catch (e: Throwable) {
                            actionMessage = e.message ?: "Переоценка не удалась"
                        } finally {
                            busy = false
                        }
                    }
                }
                Box(Modifier.width(8.px))
                ActionButton("Запустить сбор", enabled = !busy) {
                    busy = true
                    actionMessage = "Собираю фиды…"
                    scope.launch {
                        try {
                            val report = repo.runNewsIngest()
                            actionMessage = "+${report.created} черновиков (${report.rewritten} с AI)"
                            loadState.retry()
                        } catch (e: Throwable) {
                            actionMessage = e.message ?: "Сбор не удался"
                        } finally {
                            busy = false
                        }
                    }
                }
            }
            VSpace(14)

            // ── Editor or queue ──
            val current = editing
            if (current != null) {
                NewsEditor(
                    article = current,
                    busy = busy,
                    onBusy = { busy = it },
                    onMessage = { actionMessage = it },
                    onClose = { changed ->
                        editing = null
                        if (changed) loadState.retry()
                    },
                )
            } else {
                val rows = items

                fun archive(row: NewsArticleRow) {
                    busy = true
                    actionMessage = null
                    scope.launch {
                        try {
                            repo.setNewsStatus(row.id, "ARCHIVED")
                            loadState.retry()
                        } catch (e: Throwable) {
                            actionMessage = e.message ?: "Не удалось отправить в архив"
                        } finally {
                            busy = false
                        }
                    }
                }

                fun reorderFeatured(newOrder: List<NewsArticleRow>) {
                    busy = true
                    actionMessage = null
                    scope.launch {
                        try {
                            items = repo.reorderFeaturedNews(newOrder.map { it.id })
                        } catch (e: Throwable) {
                            actionMessage = e.message ?: "Не удалось изменить порядок"
                        } finally {
                            busy = false
                        }
                    }
                }

                when {
                    rows == null -> SpanText("Загрузка…", Modifier.bodyMRegular().color(AppColors.Muted))
                    rows.isEmpty() -> SpanText(
                        if (statusTab == FEATURED_TAB) {
                            "Пусто — открой статью из «Draft»/«Published» и нажми «Добавить в обсуждаемое»"
                        } else {
                            "Здесь пусто"
                        },
                        Modifier.bodyMRegular().color(AppColors.Muted),
                    )
                    statusTab == FEATURED_TAB -> rows.forEachIndexed { index, row ->
                        FeaturedRow(
                            row = row,
                            position = index + 1,
                            isFirst = index == 0,
                            isLast = index == rows.lastIndex,
                            busy = busy,
                            onOpen = { editing = row },
                            onMove = { delta ->
                                val target = index + delta
                                if (target in rows.indices) {
                                    val next = rows.toMutableList()
                                    next[index] = rows[target]
                                    next[target] = rows[index]
                                    reorderFeatured(next)
                                }
                            },
                            onRemove = { reorderFeatured(rows.filter { it.id != row.id }) },
                        )
                    }
                    else -> rows.forEach { row ->
                        NewsListRow(
                            row = row,
                            busy = busy,
                            onArchive = if (row.status != "ARCHIVED") ({ archive(row) }) else null,
                            onOpen = { editing = row },
                        )
                    }
                }

                if (statusTab != FEATURED_TAB && totalPages > 1) {
                    VSpace(12)
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        ActionButton("←", enabled = page > 1) { page-- }
                        SpanText(
                            "  $page / $totalPages  ",
                            Modifier.bodySMedium().color(AppColors.Muted),
                        )
                        ActionButton("→", enabled = page < totalPages) { page++ }
                    }
                }
            }
        }
    }
}

// ── Queue row ────────────────────────────────────────────────────────────────

@Composable
private fun NewsListRow(
    row: NewsArticleRow,
    busy: Boolean,
    onArchive: (() -> Unit)?,
    onOpen: () -> Unit,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .sBorderRadius(12)
            .padding(10.px)
            .margin(bottom = 8.px)
            .styleModifier {
                property("background", "#0F1626")
                property("border", "1px solid rgba(255,255,255,0.06)")
                property("cursor", "pointer")
            }
            .onClick { onOpen() },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        if (row.coverImage.isNotBlank()) {
            Img(
                src = row.coverImage,
                attrs = Modifier
                    .width(72.px).height(48.px)
                    .sBorderRadius(8)
                    .margin(right = 12.px)
                    .styleModifier { property("object-fit", "cover"); property("flex-shrink", "0") }
                    .toAttrs(),
            )
        }
        Column(Modifier.weight(1f).styleModifier { property("min-width", "0") }) {
            SpanText(
                row.title,
                Modifier.bodyMMedium().color(AppColors.Ink).styleModifier {
                    property("display", "-webkit-box")
                    property("-webkit-line-clamp", "2")
                    property("-webkit-box-orient", "vertical")
                    property("overflow", "hidden")
                },
            )
            VSpace(4)
            SpanText(
                "${row.sourceName} · ${row.sport} · ${row.createdAt.take(10)} · ${row.viewsCount} 👁",
                Modifier.bodySMedium().color(AppColors.Muted),
            )
        }
        // AI importance: 8+ = top story, 6-7 = notable; routine stays quiet.
        if (row.importance >= 6) {
            SpanText(
                if (row.importance >= 8) "🔥 ${row.importance}" else "★ ${row.importance}",
                Modifier
                    .bodySMedium()
                    .color(if (row.importance >= 8) com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xFFB454) else AppColors.Ink)
                    .margin(left = 12.px)
                    .styleModifier { property("flex-shrink", "0") },
            )
        }
        Pill(
            row.status.lowercase(),
            when (row.status) {
                "PUBLISHED" -> PillTone.Success
                "ARCHIVED" -> PillTone.Neutral
                else -> PillTone.Accent
            },
            Modifier.margin(left = 12.px),
        )
        // Direct "archive" — stops propagation so the row's open-editor click
        // doesn't also fire.
        if (onArchive != null) {
            Box(
                Modifier
                    .sBorderRadius(999)
                    .padding(leftRight = 12.px, topBottom = 6.px)
                    .margin(left = 12.px)
                    .styleModifier {
                        property("background", "#101828")
                        property("border", "1px solid rgba(255,255,255,0.10)")
                        property("cursor", if (busy) "not-allowed" else "pointer")
                        property("white-space", "nowrap")
                        property("flex-shrink", "0")
                        if (busy) property("opacity", "0.4")
                    }
                    .onClick { evt ->
                        evt.stopPropagation()
                        if (!busy) onArchive()
                    },
            ) {
                SpanText("В архив", Modifier.bodySMedium().color(AppColors.Muted))
            }
        }
    }
}

// ── "Обсуждаемое" (curated strip) row: slot number + reorder / remove ────────

@Composable
private fun FeaturedRow(
    row: NewsArticleRow,
    position: Int,
    isFirst: Boolean,
    isLast: Boolean,
    busy: Boolean,
    onOpen: () -> Unit,
    onMove: (delta: Int) -> Unit,
    onRemove: () -> Unit,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .sBorderRadius(12)
            .padding(10.px)
            .margin(bottom = 8.px)
            .styleModifier {
                property("background", "#0F1626")
                property("border", "1px solid rgba(255,255,255,0.06)")
                property("cursor", "pointer")
            }
            .onClick { onOpen() },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            Modifier.width(28.px).styleModifier { property("flex-shrink", "0") },
            contentAlignment = Alignment.Center,
        ) {
            SpanText(
                "$position",
                Modifier.bodyMMedium().color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x8FB8FF)),
            )
        }
        if (row.coverImage.isNotBlank()) {
            Img(
                src = row.coverImage,
                attrs = Modifier
                    .width(72.px).height(48.px)
                    .sBorderRadius(8)
                    .margin(leftRight = 10.px)
                    .styleModifier { property("object-fit", "cover"); property("flex-shrink", "0") }
                    .toAttrs(),
            )
        } else {
            Box(Modifier.width(10.px))
        }
        Column(Modifier.weight(1f).styleModifier { property("min-width", "0") }) {
            SpanText(
                row.title,
                Modifier.bodyMMedium().color(AppColors.Ink).styleModifier {
                    property("display", "-webkit-box")
                    property("-webkit-line-clamp", "2")
                    property("-webkit-box-orient", "vertical")
                    property("overflow", "hidden")
                },
            )
            VSpace(4)
            SpanText(
                "${row.sport} · ${row.status.lowercase()} · ${row.viewsCount} 👁",
                Modifier.bodySMedium().color(AppColors.Muted),
            )
        }
        // Published-only strip: flag anything that won't actually show.
        if (row.status != "PUBLISHED") {
            SpanText(
                "не в публикации",
                Modifier.bodySMedium().color(AppColors.Error).margin(left = 10.px).styleModifier {
                    property("flex-shrink", "0")
                },
            )
        }
        RowIconButton("↑", enabled = !busy && !isFirst) { onMove(-1) }
        RowIconButton("↓", enabled = !busy && !isLast) { onMove(1) }
        RowIconButton("✕", enabled = !busy, danger = true) { onRemove() }
    }
}

@Composable
private fun RowIconButton(
    glyph: String,
    enabled: Boolean,
    danger: Boolean = false,
    onClick: () -> Unit,
) {
    Box(
        Modifier
            .sBorderRadius(8)
            .width(32.px).height(32.px)
            .margin(left = 6.px)
            .styleModifier {
                property("background", if (danger) AppColors.Error.toRgbaString(0.12f) else "#101828")
                property(
                    "border",
                    if (danger) "1px solid ${AppColors.Error.toRgbaString(0.30f)}" else "1px solid rgba(255,255,255,0.10)",
                )
                property("cursor", if (enabled) "pointer" else "not-allowed")
                property("flex-shrink", "0")
                if (!enabled) property("opacity", "0.35")
            }
            .onClick { evt ->
                evt.stopPropagation()
                if (enabled) onClick()
            },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(glyph, Modifier.bodyMMedium().color(if (danger) AppColors.Error else AppColors.Ink))
    }
}

// ── Editor ───────────────────────────────────────────────────────────────────

@Composable
private fun NewsEditor(
    article: NewsArticleRow,
    busy: Boolean,
    onBusy: (Boolean) -> Unit,
    onMessage: (String?) -> Unit,
    onClose: (changed: Boolean) -> Unit,
) {
    val repo = LocalAppContainer.current.adminRepository ?: return
    val scope = rememberCoroutineScope()

    var title by remember { mutableStateOf(article.title) }
    var summary by remember { mutableStateOf(article.summary) }
    var body by remember { mutableStateOf(article.body) }
    var sport by remember { mutableStateOf(article.sport) }
    var type by remember { mutableStateOf(article.type) }
    var hashtags by remember { mutableStateOf(article.hashtags.joinToString(", ")) }
    var cover by remember { mutableStateOf(article.coverImage) }
    var candidates by remember { mutableStateOf<NewsImageCandidates?>(null) }
    var uploading by remember { mutableStateOf(false) }
    var featuredRank by remember { mutableStateOf(article.featuredRank) }
    // Adding/removing from the strip changes the "Обсуждаемое" tab — signal a
    // reload on close so that tab isn't stale.
    var featuredDirty by remember { mutableStateOf(false) }
    // Publish-time: also broadcast a localized push to every subscriber.
    var notifyAll by remember { mutableStateOf(false) }

    var matchLink by remember { mutableStateOf(article.match) }
    var matchQuery by remember { mutableStateOf("") }
    var matchDate by remember {
        mutableStateOf((article.publishedAt ?: article.createdAt).take(10))
    }
    var matchResults by remember { mutableStateOf<List<NewsMatchLink>>(emptyList()) }

    fun payload() = SaveNewsArticlePayload(
        title = title,
        summary = summary,
        body = body,
        coverImage = cover,
        sport = sport,
        type = type,
        sourceName = article.sourceName,
        sourceUrl = article.sourceUrl,
        locale = article.locale,
        hashtags = hashtags.split(",").map { it.trim().lowercase() }.filter { it.isNotEmpty() },
    )

    fun run(message: String, block: suspend () -> Unit) {
        onBusy(true)
        onMessage(null)
        scope.launch {
            try {
                block()
                onMessage(message)
            } catch (e: Throwable) {
                onMessage(e.message ?: "Не получилось")
                onBusy(false)
                return@launch
            }
            onBusy(false)
        }
    }

    // Поиск кандидатов: дата ±1 день на бэке, токены q — по названиям команд.
    LaunchedEffect(matchQuery, matchDate, sport, matchLink) {
        if (matchLink != null || matchDate.length != 10) {
            matchResults = emptyList()
            return@LaunchedEffect
        }
        delay(300)
        matchResults = try {
            repo.searchNewsMatches(sport, matchDate, matchQuery)
        } catch (e: Throwable) {
            emptyList()
        }
    }

    Column(Modifier.fillMaxWidth()) {
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            ActionButton("← К списку", enabled = !busy) { onClose(featuredDirty) }
            // Публикация — сверху, чтобы не скроллить весь редактор вниз.
            if (article.status != "PUBLISHED") {
                Box(Modifier.width(8.px))
                NotifyAllToggle(checked = notifyAll, onChange = { notifyAll = it })
                Box(Modifier.width(8.px))
                ActionButton("Опубликовать (+переводы, ~25с)", enabled = !busy, primary = true) {
                    run(if (notifyAll) "Опубликовано + пуш 🎉" else "Опубликовано 🎉") {
                        repo.updateNewsArticle(article.id, payload())
                        repo.setNewsStatus(article.id, "PUBLISHED", notify = notifyAll)
                        onClose(true)
                    }
                }
            }
            Box(Modifier.weight(1f))
            SpanText(
                "${article.status} · ${article.sourceName}",
                Modifier.bodySMedium().color(AppColors.Muted).margin(right = 10.px),
            )
            org.jetbrains.compose.web.dom.A(
                href = article.sourceUrl,
                attrs = {
                    attr("target", "_blank")
                    attr("rel", "noopener")
                    style { property("color", "#8FB8FF"); property("font-size", "13px") }
                },
            ) { org.jetbrains.compose.web.dom.Text("источник ↗") }
        }
        VSpace(14)

        // ── Cover — первым делом: крупное превью без обрезки + кнопки. ──
        FieldLabel("Обложка")
        VSpace(6)
        if (cover.isNotBlank()) {
            Img(
                src = cover,
                attrs = Modifier
                    .fillMaxWidth().height(320.px)
                    .sBorderRadius(12)
                    // contain на тёмной подложке: админ оценивает ПОЛНЫЙ кадр,
                    // а не вырезку.
                    .styleModifier {
                        property("object-fit", "contain")
                        property("background", "#060E1E")
                    }
                    .toAttrs(),
            )
            VSpace(8)
        } else {
            SpanText(
                "Без обложки — сайт подставит спортивный арт",
                Modifier.bodySMedium().color(AppColors.Muted),
            )
            VSpace(8)
        }
        Row {
            ActionButton("Подобрать фото", enabled = !busy) {
                scope.launch {
                    try {
                        candidates = repo.getNewsImageCandidates(article.id)
                    } catch (e: Throwable) {
                        onMessage(e.message ?: "Кандидаты не загрузились")
                    }
                }
            }
            Box(Modifier.width(8.px))
            ActionButton(if (uploading) "Загружаю…" else "Загрузить свою", enabled = !uploading) {
                pickImageFile { bytes, fileName, contentType ->
                    uploading = true
                    scope.launch {
                        try {
                            cover = repo.uploadNewsImage(bytes, fileName, contentType)
                        } catch (e: Throwable) {
                            onMessage(e.message ?: "Загрузка не удалась")
                        } finally {
                            uploading = false
                        }
                    }
                }
            }
            Box(Modifier.width(8.px))
            ActionButton("Спортивный арт", enabled = cover.isNotBlank()) { cover = "" }
        }

        candidates?.let { cand ->
            VSpace(12)
            cand.sourcePreview?.let { preview ->
                SpanText(
                    "Фото источника — ТОЛЬКО превью (лицензия издателя, не публиковать):",
                    Modifier.bodySMedium().color(AppColors.Error),
                )
                VSpace(6)
                Img(
                    src = preview,
                    attrs = Modifier
                        .width(220.px).height(124.px)
                        .sBorderRadius(10)
                        .styleModifier { property("object-fit", "cover"); property("opacity", "0.85") }
                        .toAttrs(),
                )
                VSpace(10)
            }
            if (cand.stock.isNotEmpty()) {
                SpanText("Сток Pexels (свободная лицензия — клик выбирает):", Modifier.bodySMedium().color(AppColors.Muted))
                VSpace(6)
                Div(
                    attrs = {
                        style {
                            property("display", "grid")
                            property("grid-template-columns", "repeat(3, 180px)")
                            property("gap", "8px")
                        }
                    },
                ) {
                    cand.stock.forEach { url ->
                        Img(
                            src = url,
                            attrs = Modifier
                                .width(180.px).height(102.px)
                                .sBorderRadius(10)
                                .styleModifier {
                                    property("object-fit", "cover")
                                    property("cursor", "pointer")
                                    if (url == cover) property("outline", "2px solid ${AppColors.PrimaryMint.toRgbaString(1f)}")
                                }
                                .onClick { cover = url }
                                .toAttrs(),
                        )
                    }
                }
            } else {
                SpanText("Сток пуст (нет PEXELS_API_KEY или совпадений)", Modifier.bodySMedium().color(AppColors.Muted))
            }
        }
        VSpace(16)

        // «Обсуждаемое»: добавление кидает статью в КОНЕЦ списка; порядок
        // (какой слот) правится во вкладке «Обсуждаемое», не тут.
        FieldLabel("«Обсуждаемое» (порядок задаётся во вкладке)")
        VSpace(4)
        Row(verticalAlignment = Alignment.CenterVertically) {
            if (featuredRank == null) {
                ActionButton("Добавить в обсуждаемое", enabled = !busy) {
                    onBusy(true)
                    onMessage(null)
                    scope.launch {
                        try {
                            val updated = repo.appendFeaturedNews(article.id)
                            featuredRank = updated.featuredRank
                            featuredDirty = true
                            onMessage("Добавлено — порядок правится во вкладке «Обсуждаемое»")
                        } catch (e: Throwable) {
                            onMessage(e.message ?: "Не удалось добавить")
                        } finally {
                            onBusy(false)
                        }
                    }
                }
            } else {
                SpanText(
                    "В обсуждаемом · слот $featuredRank",
                    Modifier.bodySMedium().color(AppColors.Ink).margin(right = 10.px),
                )
                ActionButton("Убрать", enabled = !busy) {
                    onBusy(true)
                    onMessage(null)
                    scope.launch {
                        try {
                            repo.setNewsFeaturedRank(article.id, null)
                            featuredRank = null
                            featuredDirty = true
                            onMessage("Убрано из обсуждаемого")
                        } catch (e: Throwable) {
                            onMessage(e.message ?: "Не удалось убрать")
                        } finally {
                            onBusy(false)
                        }
                    }
                }
            }
        }
        VSpace(16)

        // ── Привязанный матч: карточка + хайлайт на странице статьи. ──
        FieldLabel("Матч (карточка и видео-хайлайт в статье)")
        VSpace(4)
        matchLink?.let { m ->
            Row(verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    "${m.homeTeam} — ${m.awayTeam} · ${m.utcDate.take(10)} · ${m.competitionName}",
                    Modifier.bodySMedium().color(AppColors.Ink).margin(right = 10.px),
                )
                ActionButton("Отвязать", enabled = !busy) {
                    run("Матч отвязан") {
                        repo.setNewsMatchLink(article.id, null)
                        matchLink = null
                    }
                }
            }
        }
        if (matchLink == null) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Column {
                    FieldLabel("Дата (yyyy-MM-dd, ищем ±1 день)")
                    VSpace(4)
                    DarkInput(matchDate) { matchDate = it }
                }
                Box(Modifier.width(12.px))
                Column(Modifier.weight(1f)) {
                    FieldLabel("Команды (например: arsenal chelsea)")
                    VSpace(4)
                    DarkInput(matchQuery) { matchQuery = it }
                }
            }
            matchResults.take(8).forEach { m ->
                VSpace(6)
                Row(verticalAlignment = Alignment.CenterVertically) {
                    SpanText(
                        "${m.utcDate.take(16).replace("T", " ")} · ${m.homeTeam} — ${m.awayTeam} · ${m.competitionName}",
                        Modifier.bodySMedium().color(AppColors.Muted).margin(right = 10.px),
                    )
                    ActionButton("Привязать", enabled = !busy) {
                        run("Матч привязан") {
                            repo.setNewsMatchLink(article.id, m)
                            matchLink = m
                        }
                    }
                }
            }
        }
        VSpace(16)

        FieldLabel("Заголовок (EN — мастер; переводы генерятся при публикации)")
        VSpace(4)
        DarkInput(title) { title = it }
        VSpace(12)

        FieldLabel("Саммари")
        VSpace(4)
        DarkTextArea(summary, rows = 3) { summary = it }
        VSpace(12)

        FieldLabel("Текст")
        VSpace(4)
        DarkTextArea(body, rows = 12) { body = it }
        VSpace(12)

        Row {
            Column {
                FieldLabel("Спорт")
                VSpace(4)
                DarkSelect(sport, SPORTS) { sport = it }
            }
            Box(Modifier.width(16.px))
            Column {
                FieldLabel("Тип")
                VSpace(4)
                DarkSelect(type, TYPES) { type = it }
            }
        }
        VSpace(12)

        FieldLabel("Хештеги (через запятую, en-слаги)")
        VSpace(4)
        DarkInput(hashtags) { hashtags = it }
        VSpace(16)

        VSpace(18)

        // ── Actions ──
        Row {
            ActionButton("Сохранить", enabled = !busy, primary = true) {
                run("Сохранено") { repo.updateNewsArticle(article.id, payload()) }
            }
            Box(Modifier.width(8.px))
            if (article.status == "PUBLISHED") {
                ActionButton("Снять с публикации", enabled = !busy) {
                    run("Возвращено в черновики") {
                        repo.setNewsStatus(article.id, "DRAFT")
                        onClose(true)
                    }
                }
                Box(Modifier.width(8.px))
            }
            if (article.status != "ARCHIVED") {
                ActionButton("В архив", enabled = !busy) {
                    run("В архиве") {
                        repo.setNewsStatus(article.id, "ARCHIVED")
                        onClose(true)
                    }
                }
                Box(Modifier.width(8.px))
            }
            ActionButton("Удалить", enabled = !busy, danger = true) {
                run("Удалено") {
                    repo.deleteNewsArticle(article.id)
                    onClose(true)
                }
            }
        }
    }
}

// ── Local widgets (AppLaunch style) ─────────────────────────────────────────

@Composable
private fun FieldLabel(text: String) {
    SpanText(text, Modifier.bodySMedium().color(AppColors.Muted))
}

@Composable
private fun ActionButton(
    label: String,
    enabled: Boolean,
    primary: Boolean = false,
    danger: Boolean = false,
    onClick: () -> Unit,
) {
    Box(
        Modifier
            .sBorderRadius(999)
            .padding(leftRight = 16.px, topBottom = 8.px)
            .styleModifier {
                property(
                    "background",
                    when {
                        danger -> AppColors.Error.toRgbaString(0.12f)
                        primary -> "#0A1E42"
                        else -> "#101828"
                    },
                )
                property(
                    "border",
                    when {
                        danger -> "1px solid ${AppColors.Error.toRgbaString(0.30f)}"
                        primary -> "1px solid rgba(143,184,255,0.45)"
                        else -> "1px solid rgba(255,255,255,0.10)"
                    },
                )
                if (!enabled) property("opacity", "0.4")
                property("cursor", if (enabled) "pointer" else "not-allowed")
                property("white-space", "nowrap")
            }
            .onClick { if (enabled) onClick() },
    ) {
        SpanText(
            label,
            Modifier.bodySMedium().color(
                when {
                    danger -> AppColors.Error
                    primary -> com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x8FB8FF)
                    else -> AppColors.Ink
                },
            ),
        )
    }
}

@Composable
private fun DarkInput(value: String, onChange: (String) -> Unit) {
    Input(
        type = InputType.Text,
        attrs = Modifier.toAttrs {
            value(value)
            onInput { onChange(it.value) }
            style {
                property("width", "100%")
                property("max-width", "720px")
                property("box-sizing", "border-box")
                property("background", INPUT_BG)
                property("border", INPUT_BORDER)
                property("border-radius", "10px")
                property("color", "#E7EAF0")
                property("padding", "10px 12px")
                property("font-size", "14px")
                property("outline", "none")
            }
        },
    )
}

@Composable
private fun DarkTextArea(value: String, rows: Int, onChange: (String) -> Unit) {
    TextArea(
        attrs = {
            value(value)
            attr("rows", rows.toString())
            onInput { onChange(it.value) }
            style {
                property("width", "100%")
                property("max-width", "720px")
                property("box-sizing", "border-box")
                property("background", INPUT_BG)
                property("border", INPUT_BORDER)
                property("border-radius", "10px")
                property("color", "#E7EAF0")
                property("padding", "10px 12px")
                property("font-size", "14px")
                property("font-family", "inherit")
                property("outline", "none")
                property("resize", "vertical")
            }
        },
    )
}

@Composable
private fun DarkSelect(value: String, options: List<String>, onChange: (String) -> Unit) {
    Select(
        attrs = {
            onChange { it.value?.let(onChange) }
            style {
                property("background", INPUT_BG)
                property("border", INPUT_BORDER)
                property("border-radius", "10px")
                property("color", "#E7EAF0")
                property("padding", "10px 12px")
                property("font-size", "14px")
                property("outline", "none")
            }
        },
    ) {
        options.forEach { option ->
            Option(option, attrs = { if (option == value) attr("selected", "true") }) {
                org.jetbrains.compose.web.dom.Text(option)
            }
        }
    }
}

/** Publish-time toggle: broadcast a localized push to every subscriber. */
@Composable
private fun NotifyAllToggle(checked: Boolean, onChange: (Boolean) -> Unit) {
    Box(
        Modifier
            .sBorderRadius(999)
            .padding(leftRight = 14.px, topBottom = 7.px)
            .styleModifier {
                property("background", if (checked) "#F0F4FC" else INPUT_BG)
                property("border", "1px solid rgba(255,255,255,0.12)")
                property("cursor", "pointer")
            }
            .onClick { onChange(!checked) },
    ) {
        SpanText(
            if (checked) "🔔 Пуш всем: вкл" else "🔕 Пуш всем: выкл",
            Modifier.bodySMedium().color(if (checked) AppColors.Surface else AppColors.Ink),
        )
    }
}
